// EWM-833 — how eagerly the header search autocomplete queries Algolia.
//
// Both knobs live here rather than as literals in the component, because they
// are the two numbers most likely to be retuned and they are meaningless in
// isolation — a reviewer needs to see them together.
//
// Before this, the autocomplete debounced 150ms and had NO minimum length, so
// typing "battery" fired seven searches, the first two of which ("b", "ba")
// match near-arbitrarily across a ~9,400-product index and cannot be useful.
// Algolia bills per search request, and those throwaway queries were also
// recorded as real searches, polluting the analytics that Query Suggestions and
// "top searches" are built from.
//
// Both are overridable per environment, same convention as
// NEXT_PUBLIC_MIN_ORDER_THRESHOLD_CENTS in `config/min-order.ts`:
//
//   NEXT_PUBLIC_SEARCH_AUTOCOMPLETE_MIN_CHARS    (default 3)
//   NEXT_PUBLIC_SEARCH_AUTOCOMPLETE_DEBOUNCE_MS  (default 1000)
//
// ⚠ `NEXT_PUBLIC_*` is INLINED INTO THE CLIENT BUNDLE AT BUILD TIME. Changing
// either variable takes effect only after a REBUILD — and because Netlify
// auto-publish is locked, that means a Frontastic deploy. The override buys you
// a retune without a code change and a PR; it does NOT buy you one without a
// deploy.
//
// NOTE this governs the live autocomplete ONLY. Submitting the form (Enter or
// "View all results") navigates to /search at any length and is deliberately
// unaffected — a shopper who types two characters and presses Enter still gets
// a results page.

const DEFAULT_MIN_CHARS = 3;
const DEFAULT_DEBOUNCE_MS = 1000;

/**
 * Read a numeric env override, falling back on anything that is not a
 * non-negative integer.
 *
 * The validation is load-bearing rather than defensive dressing. An unparseable
 * value yields `NaN`, and every `length >= NaN` comparison is `false` — so a
 * single typo in the variable would silently disable the autocomplete for every
 * shopper, with no error raised anywhere and search simply never appearing.
 * Falling back (loudly) keeps a bad value from taking search down.
 *
 * `name` is passed separately because Next.js only inlines `process.env.FOO`
 * written out in full: a dynamic `process.env[name]` lookup is NOT substituted
 * at build time and would read as `undefined` in the browser.
 */
const readCount = (name: string, raw: string | undefined, fallback: number): number => {
  if (raw === undefined || raw.trim() === '') return fallback;

  const parsed = Number(raw);
  if (!Number.isInteger(parsed) || parsed < 0) {
    console.warn(`[search-tuning] ignoring ${name}="${raw}" — expected a non-negative integer; using ${fallback}`);
    return fallback;
  }

  return parsed;
};

/**
 * Characters required before the first request goes out.
 *
 * Raising this reduces requests but delays the dropdown; lowering it toward 1
 * reinstates the noise described above. 3 is the point where a query starts to
 * be discriminating on this catalogue.
 */
export const SEARCH_AUTOCOMPLETE_MIN_CHARS = readCount(
  'NEXT_PUBLIC_SEARCH_AUTOCOMPLETE_MIN_CHARS',
  process.env.NEXT_PUBLIC_SEARCH_AUTOCOMPLETE_MIN_CHARS,
  DEFAULT_MIN_CHARS,
);

/**
 * Idle time after the last keystroke before the request fires, in ms.
 *
 * `useDebounce` restarts its timer on every change, so a shopper typing
 * continuously sends nothing until they pause — the pending request is replaced
 * rather than queued.
 *
 * Floored at 1: `useDebounce` applies its own default with `delay || 500`, so a
 * literal 0 would come out as 500ms — the opposite of the "no debounce" whoever
 * set it was asking for. 1ms is indistinguishable from none, and correcting the
 * shared hook would change behaviour for its three other callers.
 */
export const SEARCH_AUTOCOMPLETE_DEBOUNCE_MS = Math.max(
  1,
  readCount(
    'NEXT_PUBLIC_SEARCH_AUTOCOMPLETE_DEBOUNCE_MS',
    process.env.NEXT_PUBLIC_SEARCH_AUTOCOMPLETE_DEBOUNCE_MS,
    DEFAULT_DEBOUNCE_MS,
  ),
);

/**
 * Whether a query has enough characters to be worth sending.
 *
 * Trims first: the length that matters is what would actually reach Algolia,
 * and it keeps "  ab  " from passing a raw `.length >= 3` check. Mirrors the
 * trimming in `isSearchQueryTooLong` so both gates measure the same string.
 */
export const isSearchQueryLongEnough = (query: string): boolean => query.trim().length >= SEARCH_AUTOCOMPLETE_MIN_CHARS;
